import { collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc, getDoc, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '../config';
import { startOfDay, endOfDay, subDays, format } from 'date-fns';
import { entrySource, MANUAL, type EntrySource, type HealthPlatform } from '../../health/healthImportModel';
import { recordImportTombstone } from './healthImport';

export interface SleepLog {
  id: string;
  userId: string;
  date: Date; // Sleep date (night of)
  sleepTime: Date; // When user went to sleep
  awakenTime: Date; // When user woke up
  hours: number; // Hours of sleep (calculated)
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
  /**
   * Where this entry came from. A stored value that is absent — or anything
   * other than 'health' — classifies as manual, exactly as it does on mobile;
   * see entrySource.
   */
  source?: EntrySource;
  /** Imported entries only. */
  healthPlatform?: HealthPlatform;
  /** Imported entries only: the raw platform record id. */
  healthRecordId?: string;
}

export interface CreateSleepLogParams {
  userId: string;
  date: Date;
  sleepTime: Date;
  awakenTime: Date;
  notes?: string;
}

export interface UpdateSleepLogParams {
  date?: Date;
  sleepTime?: Date;
  awakenTime?: Date;
  notes?: string;
}

// Calculate hours from sleep and awaken times
function calculateSleepHours(sleepTime: Date, awakenTime: Date): number {
  const diff = awakenTime.getTime() - sleepTime.getTime();
  return diff / (1000 * 60 * 60); // Convert to hours
}

// Get sleep logs for the last 7 days
export async function getSleepLogsLastWeek(userId: string): Promise<SleepLog[]> {
  try {
    const endDate = new Date();
    const startDate = subDays(endDate, 7);
    
    const sleepQuery = query(
      collection(db, 'sleepLogs'),
      where('userId', '==', userId),
      where('date', '>=', Timestamp.fromDate(startDate)),
      where('date', '<=', Timestamp.fromDate(endDate)),
      orderBy('date', 'desc')
    );
    
    const snapshot = await getDocs(sleepQuery);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      const sleepTime = data.sleepTime?.toDate() || data.date.toDate();
      const awakenTime = data.awakenTime?.toDate() || new Date();
      const hours = data.hours || calculateSleepHours(sleepTime, awakenTime);
      
      return {
        id: doc.id,
        userId: data.userId,
        date: data.date.toDate(),
        sleepTime,
        awakenTime,
        hours,
        notes: data.notes,
        // Normalised, never raw: an absent source reads as manual here exactly
        // as it does on the phone, so one document cannot classify two ways.
        source: entrySource(data.source),
        healthPlatform: data.healthPlatform,
        healthRecordId: data.healthRecordId,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      };
    });
  } catch (error) {
    console.error('Error fetching sleep logs:', error);
    throw error;
  }
}

// Get sleep logs by date range
export async function getSleepLogsByDateRange(
  userId: string,
  startDate: Date,
  endDate: Date
): Promise<SleepLog[]> {
  try {
    const sleepQuery = query(
      collection(db, 'sleepLogs'),
      where('userId', '==', userId),
      where('date', '>=', Timestamp.fromDate(startDate)),
      where('date', '<=', Timestamp.fromDate(endDate)),
      orderBy('date', 'desc')
    );
    
    const snapshot = await getDocs(sleepQuery);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      const sleepTime = data.sleepTime?.toDate() || data.date.toDate();
      const awakenTime = data.awakenTime?.toDate() || new Date();
      const hours = data.hours || calculateSleepHours(sleepTime, awakenTime);
      
      return {
        id: doc.id,
        userId: data.userId,
        date: data.date.toDate(),
        sleepTime,
        awakenTime,
        hours,
        notes: data.notes,
        // Normalised, never raw: an absent source reads as manual here exactly
        // as it does on the phone, so one document cannot classify two ways.
        source: entrySource(data.source),
        healthPlatform: data.healthPlatform,
        healthRecordId: data.healthRecordId,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      };
    });
  } catch (error) {
    console.error('Error fetching sleep logs by date range:', error);
    throw error;
  }
}

// Create a sleep log
export async function createSleepLog(params: CreateSleepLogParams): Promise<SleepLog> {
  try {
    const hours = calculateSleepHours(params.sleepTime, params.awakenTime);
    const sleepData = {
      userId: params.userId,
      date: Timestamp.fromDate(params.date),
      sleepTime: Timestamp.fromDate(params.sleepTime),
      awakenTime: Timestamp.fromDate(params.awakenTime),
      hours,
      notes: params.notes || null,
      source: MANUAL,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const docRef = await addDoc(collection(db, 'sleepLogs'), sleepData);
    
    return {
      id: docRef.id,
      userId: params.userId,
      date: params.date,
      sleepTime: params.sleepTime,
      awakenTime: params.awakenTime,
      hours,
      notes: params.notes,
      createdAt: new Date(),
      updatedAt: new Date(),
      // Was omitted, so an optimistic insert differed from a re-read of the
      // same document. Mobile's createSleepLog returns it; this now matches.
      source: MANUAL,
    };
  } catch (error) {
    console.error('Error creating sleep log:', error);
    throw error;
  }
}

// Update a sleep log
export async function updateSleepLog(
  sleepLogId: string,
  params: UpdateSleepLogParams
): Promise<void> {
  try {
    const updateData: any = {
      updatedAt: Timestamp.now(),
    };
    
    // Get existing log to calculate hours if needed
    let sleepTime: Date | undefined = params.sleepTime;
    let awakenTime: Date | undefined = params.awakenTime;
    
    if (params.sleepTime !== undefined || params.awakenTime !== undefined) {
      // If either time is updated, we need to recalculate hours
      // We'll need to fetch the existing log to get the other time
      const sleepRef = doc(db, 'sleepLogs', sleepLogId);
      const sleepSnap = await getDoc(sleepRef);
      if (sleepSnap.exists()) {
        const existing = sleepSnap.data();
        sleepTime = params.sleepTime || existing.sleepTime?.toDate();
        awakenTime = params.awakenTime || existing.awakenTime?.toDate();
        
        if (sleepTime && awakenTime) {
          updateData.hours = calculateSleepHours(sleepTime, awakenTime);
        }
      }
    }
    
    if (params.date !== undefined) {
      updateData.date = Timestamp.fromDate(params.date);
    }
    if (params.sleepTime !== undefined) {
      updateData.sleepTime = Timestamp.fromDate(params.sleepTime);
    }
    if (params.awakenTime !== undefined) {
      updateData.awakenTime = Timestamp.fromDate(params.awakenTime);
    }
    if (params.notes !== undefined) {
      updateData.notes = params.notes || null;
    }
    
    await updateDoc(doc(db, 'sleepLogs', sleepLogId), updateData);
  } catch (error) {
    console.error('Error updating sleep log:', error);
    throw error;
  }
}

// Delete a sleep log
export async function deleteSleepLog(sleepLogId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, 'sleepLogs', sleepLogId));
    // Without this, the phone's next import pass recreates the entry the user
    // just deleted here.
    await recordImportTombstone(sleepLogId);
  } catch (error) {
    console.error('Error deleting sleep log:', error);
    throw error;
  }
}
